(ns ui.mcp.mockups
  "Schematic mockups of the Claude.ai screens a user walks through to add
   the Storrito MCP server as a custom connector.

   They replace screenshots, which go stale with every Claude.ai
   redesign: the mockups are plain hiccup, so a change in Claude.ai is a
   small diff here, and the organization's real MCP URL can be rendered
   into them.

   Every mockup takes an options map and carries numbered `hint` markers
   on the spots the help text refers to (see the doc-string of each
   mockup for the numbering). The screens are illustrations, not UI:
   there are no event handlers, pointer events are off and the text
   cannot be selected."
  (:require [ui.mcp.hint :as hint]))

(def frame-class
  ["relative" "rounded-xl" "border" "border-stone-200" "bg-white"
   "shadow-sm" "overflow-hidden" "text-stone-800" "text-sm" "select-none"
   "pointer-events-none" "font-sans" "text-left"])

(defn- frame
  "The outer box of a mockup. `label` describes the screen for assistive
   technology, the illustration itself is decorative."
  [label & children]
  (into [:div {:class frame-class
               :role "img"
               :aria-label label}]
        children))

(defn- icon
  [path]
  [:svg {:viewBox "0 0 20 20"
         :fill "currentColor"
         :aria-hidden "true"
         :class "h-4 w-4 shrink-0"}
   [:path {:fill-rule "evenodd"
           :clip-rule "evenodd"
           :d path}]])

(def icon-check
  (icon "M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"))

(def icon-chevron-down
  (icon "M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"))

(def icon-x
  (icon "M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"))

(def icon-search
  (icon "M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z"))

(def icon-trash
  (icon "M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z"))

(def icon-plus
  (icon "M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z"))

(defn- icon-placeholder
  "Stands in for the line icons of Claude.ai's menus."
  []
  [:span {:class ["inline-block" "h-3.5" "w-3.5" "shrink-0" "rounded-sm"
                  "border" "border-stone-400"]}])

(defn- logo
  "A square app logo. `:storrito` renders the Storrito mark, anything
   else a neutral placeholder."
  [app]
  (if (= app :storrito)
    [:span {:class ["inline-flex" "h-5" "w-5" "shrink-0" "items-center"
                    "justify-center" "rounded" "bg-storrito-500" "text-white"
                    "text-[10px]" "font-bold"]}
     "S"]
    [:span {:class ["inline-block" "h-5" "w-5" "shrink-0" "rounded"
                    "bg-stone-300"]}]))

(defn- pill
  "A secondary button."
  [text]
  [:span {:class ["inline-flex" "items-center" "rounded-lg" "border"
                  "border-stone-300" "bg-white" "px-2.5" "py-1" "text-xs"
                  "font-medium" "whitespace-nowrap"]}
   text])

(defn- primary-button
  "The dark primary button of Claude.ai, with an optional hint marker."
  [text marker]
  [:span {:class ["inline-flex" "items-center" "gap-2" "rounded-lg"
                  "bg-stone-900" "px-3" "py-1.5" "text-xs" "font-medium"
                  "text-white" "whitespace-nowrap"]}
   text
   (when marker
     (hint/marker marker))])

(defn- nav-item
  [{:keys [text active? marker]}]
  [:div {:class (into ["flex" "items-center" "gap-2" "rounded-md" "px-2"
                       "py-1" "whitespace-nowrap"]
                      (if active?
                        ["bg-stone-200" "font-medium"]
                        ["text-stone-600"]))}
   (icon-placeholder)
   [:span text]
   (when marker
     [:span {:class ["ml-auto"]}
      (hint/marker marker)])])

(defn- nav-group
  [title items]
  [:div {:class ["mt-4"]}
   [:div {:class ["mb-1" "px-2" "text-xs" "text-stone-400"]}
    title]
   (into [:div {:class ["space-y-0.5"]}]
         (map nav-item items))])

(defn- nav-items
  "The entries of a navigation group. The entry named `active` is
   highlighted and carries marker 1."
  [active texts]
  (map (fn [text]
         (cond-> {:text text}
           (= text active) (assoc :active? true
                                  :marker 1)))
       texts))

(defn- settings-nav
  "The left column of the Claude.ai settings dialog. Marker 1 sits on
   the entry named `active`."
  [active]
  [:div {:class ["hidden" "w-44" "shrink-0" "border-r" "border-stone-200"
                 "p-3" "sm:block"]}
   [:div {:class ["flex" "items-center" "gap-2" "rounded-md" "border"
                  "border-stone-200" "px-2" "py-1" "text-stone-400"]}
    icon-search
    "Search"]
   (nav-group "Settings"
              (nav-items active
                         ["General" "Account" "Privacy" "Billing" "Usage"
                          "Capabilities" "Memory" "Claude Code" "Cowork"
                          "Claude in Chrome"]))
   (nav-group "Customize"
              (nav-items active
                         ["Skills" "Connectors" "Plugins"]))])

(defn- popular-card
  [app-name]
  [:div {:class ["flex" "items-center" "justify-between" "gap-2"
                 "rounded-lg" "border" "border-stone-200" "px-3" "py-2"]}
   [:span {:class ["flex" "items-center" "gap-2" "font-medium"]}
    (logo app-name)
    app-name]
   (pill "Connect")])

(defn- list-header
  []
  [:div {:class ["flex" "items-center" "justify-between" "border-b"
                 "border-stone-200" "pb-1" "text-xs" "text-stone-500"]}
   [:span "Connectors"]
   [:span "Status"]])

(defn- list-row
  [{:keys [app-name status]}]
  [:div {:class ["flex" "items-center" "justify-between" "border-b"
                 "border-stone-100" "py-2"]}
   [:span {:class ["flex" "items-center" "gap-2"]}
    (logo app-name)
    app-name]
   (case status
     :connected [:span {:class ["text-stone-500"]} icon-check]
     :connect (pill "Connect"))])

(defn- add-menu
  "The Add button with its menu unfolded. Marker 2 sits on the Add
   custom connector entry."
  []
  [:div {:class ["relative"]}
   [:span {:class ["inline-flex" "items-center" "gap-1" "rounded-lg"
                   "border" "border-stone-300" "px-2.5" "py-1" "text-xs"
                   "font-medium"]}
    "Add"
    icon-chevron-down]
   [:div {:class ["absolute" "right-0" "top-full" "z-10" "mt-1" "w-56"
                  "rounded-lg" "border" "border-stone-200" "bg-white" "py-1"
                  "shadow-lg"]}
    [:div {:class ["flex" "items-center" "justify-between" "gap-2"
                   "whitespace-nowrap" "bg-stone-100" "px-3" "py-1.5"
                   "font-medium"]}
     "Add custom connector"
     (hint/marker 2)]]])

(defn settings-connectors
  "The Claude.ai settings with the Connectors section open and the Add
   menu unfolded. Markers: 1 the Connectors entry, 2 the Add custom
   connector menu entry."
  [_]
  (frame "Claude.ai settings, Connectors section, with the Add menu open"
    [:div {:class ["flex"]}
     (settings-nav "Connectors")
     [:div {:class ["min-w-0" "flex-1" "p-4"]}
      [:div {:class ["mb-4" "flex" "items-center" "justify-between"]}
       [:span {:class ["text-base" "font-semibold"]} "Connectors"]
       [:div {:class ["flex" "items-center" "gap-3" "text-stone-500"]}
        icon-search
        (add-menu)
        icon-x]]
      [:div {:class ["mb-2" "text-xs" "font-medium" "text-stone-500"]}
       "Popular"]
      [:div {:class ["mb-4" "flex" "flex-wrap" "gap-2"]}
       (popular-card "Slack")
       (popular-card "Google Calendar")
       (popular-card "Notion")]
      (list-header)
      (list-row {:app-name "Google Drive" :status :connected})
      (list-row {:app-name "Gmail" :status :connected})
      (list-row {:app-name "GitHub" :status :connect})]]))

(defn- field
  "A text field of the dialog, with its help text below. `value` renders
   as filled-in text, `control` replaces it with real UI, for example a
   readonly input the reader can copy from."
  [{:keys [value control help marker]}]
  [:div
   [:div {:class ["flex" "items-center" "rounded-lg" "border"
                  "border-stone-300" "bg-white" "px-3" "py-2"]}
    (or control
        [:span {:class ["truncate" "font-medium"]} value])
    (when marker
      [:span {:class ["ml-auto" "pl-2"]}
       (hint/marker marker)])]
   [:p {:class ["mt-1" "text-xs" "text-stone-500"]} help]])

(defn add-custom-connector
  "The Add custom connector dialog, filled in for Storrito. Markers: 1
   the name, 2 the MCP server URL, 3 the Continue button.

   `url-field`, when given, replaces the filled-in URL with real UI. The
   page passes a readonly input with a Copy button, because readers copy
   the URL from the spot where the dialog shows it."
  [{:keys [mcp-url url-field]}]
  (frame "Claude.ai dialog: Add custom connector, filled in for Storrito"
    [:div {:class ["mx-auto" "max-w-lg" "p-5"]}
     [:div {:class ["mb-3" "flex" "items-center" "justify-between"]}
      [:span {:class ["text-lg" "font-semibold"]} "Add custom connector"]
      [:span {:class ["text-stone-500"]} icon-x]]
     [:p {:class ["mb-4" "text-stone-600"]}
      "Connect Claude to your data and tools. "
      [:span {:class ["underline"]} "Learn more about connectors"]
      " or get started with "
      [:span {:class ["underline"]} "pre-built ones"]
      "."]
     [:div {:class ["space-y-4"]}
      (field {:value "Storrito"
              :help "Shown in the connectors list."
              :marker 1})
      (field {:value mcp-url
              :control url-field
              :help "The HTTPS address where the server accepts MCP requests, for example https://mcp.example.com/mcp."
              :marker 2})]
     [:p {:class ["mt-4" "text-xs" "text-stone-500"]}
      "Only use connectors from developers you trust. Anthropic does not control which tools developers make available and cannot verify that they will work as intended or that they won't change."]
     [:div {:class ["mt-5" "flex" "justify-end" "gap-2"]}
      (pill "Cancel")
      (primary-button "Continue" 3)]]))

(defn- window-dot
  []
  [:span {:class ["inline-block" "h-2" "w-2" "rounded-full" "bg-stone-300"]}])

(defn- popup-window
  "A small browser window, as opened by Claude for the sign-in."
  [{:keys [address]} & body]
  [:div {:class ["min-w-0" "flex-1" "overflow-hidden" "rounded-lg" "border"
                 "border-stone-300" "bg-white"]}
   [:div {:class ["flex" "items-center" "gap-2" "border-b" "border-stone-200"
                  "bg-stone-100" "px-3" "py-1.5" "text-xs" "text-stone-500"]}
    [:span {:class ["flex" "gap-1"]}
     (window-dot)
     (window-dot)
     (window-dot)]
    [:span {:class ["flex-1" "truncate" "rounded" "border" "border-stone-200"
                    "bg-white" "px-2" "py-0.5"]}
     address]]
   (into [:div {:class ["p-5"]}] body)])

(defn sign-in
  "The Storrito sign-in window that Claude opens, and the authorization
   step that follows it. Markers: 1 the Storrito login, 2 the Allow
   button."
  [_]
  (frame "Storrito sign-in window and the authorization step"
    [:div {:class ["flex" "flex-col" "gap-4" "bg-stone-50" "p-4"
                   "md:flex-row"]}
     (popup-window {:address "authkit.storrito.com"}
       [:div {:class ["mb-1" "text-base" "font-semibold"]}
        "Sign in to Storrito"]
       [:p {:class ["mb-4" "text-xs" "text-stone-500"]}
        "Welcome back! Please sign in to continue."]
       [:div {:class ["mb-3" "flex" "items-center" "rounded-lg" "border"
                      "border-stone-300" "px-3" "py-2" "text-stone-400"]}
        "Email"
        [:span {:class ["ml-auto"]}
         (hint/marker 1)]]
       [:div {:class ["rounded-lg" "bg-stone-900" "px-3" "py-2" "text-center"
                      "text-xs" "font-medium" "text-white"]}
        "Continue"])
     (popup-window {:address "authkit.storrito.com"}
       [:div {:class ["mb-1" "text-base" "font-semibold"]}
        "Claude wants to access your Storrito account"]
       [:p {:class ["mb-4" "text-xs" "text-stone-500"]}
        "Claude will be able to schedule and manage posts on your behalf."]
       [:div {:class ["flex" "justify-end" "gap-2"]}
        (pill "Cancel")
        (primary-button "Allow" 2)])]))

(defn connector-connected
  "The connectors list after the sign-in, with Storrito connected.
   Marker 1 sits on the check mark in the Status column."
  [{:keys [mcp-url]}]
  (frame "Claude.ai connectors list with Storrito connected"
    [:div {:class ["p-4"]}
     (list-header)
     [:div {:class ["flex" "items-center" "justify-between" "gap-3"
                    "border-b" "border-stone-100" "py-2"]}
      [:div {:class ["flex" "min-w-0" "items-center" "gap-2"]}
       (logo :storrito)
       [:div {:class ["min-w-0"]}
        [:div {:class ["font-medium"]} "Storrito"]
        [:div {:class ["truncate" "text-xs" "text-stone-400"]} mcp-url]]]
      [:div {:class ["flex" "shrink-0" "items-center" "gap-2"]}
       [:span {:class ["text-stone-500"]} icon-check]
       (hint/marker 1)
       [:span {:class ["text-stone-400"]} "···"]]]
     (list-row {:app-name "Google Drive" :status :connected})]))

(defn- toggle
  [on?]
  [:span {:class (into ["inline-flex" "h-4" "w-7" "shrink-0" "items-center"
                        "rounded-full" "p-0.5"]
                       (if on? ["bg-stone-900"] ["bg-stone-300"]))}
   [:span {:class (into ["h-3" "w-3" "rounded-full" "bg-white"]
                        (when on? ["ml-auto"]))}]])

(defn- switch-row
  "A setting with its explanation and a switch that is turned on."
  [{:keys [title description marker]}]
  [:div {:class ["flex" "items-start" "justify-between" "gap-4" "border-b"
                 "border-stone-100" "py-3"]}
   [:div {:class ["min-w-0"]}
    [:div {:class ["font-medium"]} title]
    [:p {:class ["mt-0.5" "text-xs" "text-stone-500"]} description]]
   [:span {:class ["flex" "shrink-0" "items-center" "gap-2" "pt-0.5"]}
    (toggle true)
    (hint/marker marker)]])

(defn settings-capabilities
  "The Claude.ai settings with the Capabilities section open, where a
   user allows Claude to upload a file from the chat. Markers: 1 the
   Capabilities entry, 2 the switch for code execution, 3 the switch for
   network egress, 4 the field for the domain.

   `host` is the domain to allow. `host-field`, when given, replaces the
   filled-in domain with real UI, so that readers can copy it from the
   spot where the settings show it."
  [{:keys [host host-field]}]
  (frame "Claude.ai settings, Capabilities section, with the storage of Storrito as allowed domain"
    [:div {:class ["flex"]}
     (settings-nav "Capabilities")
     [:div {:class ["min-w-0" "flex-1" "p-4"]}
      [:div {:class ["flex" "items-center" "justify-between"]}
       [:span {:class ["text-base" "font-semibold"]}
        "Code execution and file creation"]
       [:span {:class ["text-stone-500"]} icon-x]]
      (switch-row {:title "Cloud code execution and file creation"
                   :description "Claude can execute code on a server and create and edit docs, spreadsheets, presentations, PDFs, and data reports."
                   :marker 2})
      (switch-row {:title "Allow network egress"
                   :description "Give Claude network access to install packages and libraries."
                   :marker 3})
      [:div {:class ["mt-4" "rounded-xl" "border" "border-stone-200"
                     "bg-stone-50" "p-4"]}
       [:div {:class ["flex" "items-center" "justify-between" "gap-3"]}
        [:div {:class ["min-w-0"]}
         [:div {:class ["font-medium"]} "Domain allowlist"]
         [:p {:class ["text-xs" "text-stone-500"]}
          "Choose which domains the sandbox can access"]]
        [:span {:class ["inline-flex" "shrink-0" "items-center" "gap-1"
                        "rounded-lg" "border" "border-stone-300" "bg-white"
                        "px-2.5" "py-1" "text-xs" "font-medium"
                        "whitespace-nowrap"]}
         "Package managers only"
         icon-chevron-down]]
       [:div {:class ["mt-4" "mb-1" "font-medium"]}
        "Additional allowed domains"]
       [:div {:class ["flex" "items-center" "gap-2"]}
        [:div {:class ["flex" "min-w-0" "flex-1" "items-center" "rounded-lg"
                       "border" "border-stone-300" "bg-white" "px-3" "py-2"]}
         (or host-field
             [:span {:class ["truncate" "font-medium"]} host])
         [:span {:class ["ml-auto" "pl-2"]}
          (hint/marker 4)]]
        (primary-button "Add" nil)]
       [:div {:class ["mt-2" "flex" "items-center" "justify-between" "gap-2"
                      "rounded-lg" "bg-stone-200" "px-3" "py-2"]}
        [:span {:class ["truncate"]} host]
        [:span {:class ["text-stone-500"]} icon-trash]]]]]))

(defn- menu-entry
  [text]
  [:div {:class ["flex" "items-center" "gap-2" "px-3" "py-1.5"]}
   (icon-placeholder)
   text])

(defn- connector-entry
  [{:keys [app-name on? marker]}]
  [:div {:class ["flex" "items-center" "justify-between" "px-3" "py-1.5"]}
   [:span {:class ["flex" "items-center" "gap-2"]}
    (logo app-name)
    (if (= app-name :storrito) "Storrito" app-name)]
   [:span {:class ["flex" "items-center" "gap-2"]}
    (toggle on?)
    (when marker
      (hint/marker marker))]])

(defn chat-tools-menu
  "The message composer of a chat with its tools menu open. Markers: 1
   the Storrito switch, 2 the message field."
  [_]
  (frame "Claude.ai message composer with the connectors menu open"
    [:div {:class ["bg-stone-50" "p-4"]}
     [:div {:class ["mb-2" "w-64" "rounded-xl" "border" "border-stone-200"
                    "bg-white" "py-2" "shadow-lg"]}
      (menu-entry "Add files")
      (menu-entry "Take a screenshot")
      [:div {:class ["my-1" "border-t" "border-stone-100"]}]
      [:div {:class ["px-3" "py-1" "text-xs" "text-stone-400"]} "Connectors"]
      (connector-entry {:app-name :storrito :on? true :marker 1})
      (connector-entry {:app-name "Google Drive" :on? true})
      [:div {:class ["my-1" "border-t" "border-stone-100"]}]
      (menu-entry "Manage connectors")]
     [:div {:class ["flex" "items-center" "gap-3" "rounded-2xl" "border"
                    "border-stone-200" "bg-white" "px-4" "py-3" "shadow-sm"]}
      [:span {:class ["rounded-full" "bg-stone-100" "p-0.5" "text-stone-600"]}
       icon-plus]
      [:span {:class ["flex-1" "text-stone-400"]} "Write a message..."]
      (hint/marker 2)]]))

(defn tool-permission
  "A chat turn in which Claude asks for permission to use a Storrito tool
   for the first time. Marker 1 sits on the permission buttons."
  [_]
  (frame "Claude.ai chat asking for permission to use a Storrito tool"
    [:div {:class ["space-y-4" "bg-stone-50" "p-4"]}
     [:div {:class ["flex" "justify-end"]}
      [:div {:class ["max-w-md" "rounded-2xl" "bg-stone-200" "px-4" "py-2"]}
       "Schedule this photo as an Instagram story for tomorrow at 9:00 with a link sticker to storrito.com."]]
     [:div {:class ["max-w-lg" "space-y-3"]}
      [:p "I'll schedule the story with Storrito."]
      [:div {:class ["rounded-xl" "border" "border-stone-200" "bg-white" "p-3"]}
       [:div {:class ["mb-2" "flex" "flex-wrap" "items-center" "gap-2"
                      "text-xs" "text-stone-500"]}
        (logo :storrito)
        "Storrito wants to run"
        [:code {:class ["rounded" "bg-stone-100" "px-1" "text-stone-700"]}
         "schedule-instagram-story"]]
       [:div {:class ["flex" "items-center" "gap-2"]}
        (primary-button "Allow once" nil)
        (pill "Allow always")
        (hint/marker 1)]]]]))
